import './style.css'
import React, { useState, useEffect } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import { useUserContext } from '../../context/UserContext'
import SaddlesTable from '../../components/SaddlesTable'
import Pagination from '../../components/Pagination/Pagination'
import Button from '../../components/atoms/Buttons'
import FormInput from '../../components/atoms/Input/Form-Input'
import { brandNames, formatListArray, formatString } from '../../utils'
import {
  LIST_DISCIPLINES,
  LIST_DISCIPLINES_QUERY_OPTIONS,
} from '../../apollo/queries/listDisciplines'
import { listSaddlesForAdmin } from '../../apollo/queries/listSaddles'
import { useNavigate } from 'react-router-dom'
import { DELETE_SADDLE } from '../../apollo/mutations/deleteSaddle'
import { createToast } from '../../utils/Toast'
import MainLayout from '../../components/Layout'
import { LIST_MANUFACTURERS_BASIC } from '../../apollo/queries/listManufacturers'

const Toast = createToast()

/** Aligns manufacturer table slugs with `listSaddlesForAdmin` filter args (e.g. cactus → cactussaddlery on the server). */
function saddleAdminBrandFilterKey(value) {
  const v = (value || '').toLowerCase().trim()
  if (!v) return ''
  if (v === 'cactussaddlery') return 'cactus'
  return v
}

function SaddleAdmin() {
  const navigate = useNavigate()
  const { userData } = useUserContext()
  const isSimpleAdmin =
    userData?.brand !== 'saddlefitadmin' && (userData?.type === 'Admin' || userData?.type === 'Member')
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null })
  const [currentlyDeleting, setCurrentlyDeleting] = useState(null)
  const { data: listManufacturersData } = useQuery(LIST_MANUFACTURERS_BASIC)

  const brandOptions = React.useMemo(() => {
    const fromApi = (listManufacturersData?.listManufacturers ?? [])
      .filter(m => m?.brand)
      .map(m => ({
        value: m.brand,
        name: m.brand,
        label: formatString(m.brand),
      }))
    const merged = [
      ...brandNames.map(b => ({
        value: b.value,
        name: b.value,
        label: b.label,
      })),
      ...fromApi,
    ]
    const seen = new Set()
    return merged.filter(opt => {
      const dedupeKey = saddleAdminBrandFilterKey(opt?.value)
      if (!dedupeKey) return false
      if (seen.has(dedupeKey)) return false
      seen.add(dedupeKey)
      return true
    })
  }, [listManufacturersData])

  const [discipline, setDiscipline] = useState([])
  const [brandName, setBrandName] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  const [deleteSaddle, { loading: deleteSaddleLoading }] =
    useMutation(DELETE_SADDLE)

  const { data, loading, error: listSaddlesError } = useQuery(listSaddlesForAdmin, {
    variables: {
      ...(brandName && { brandName }),
      ...(discipline &&
        discipline.length > 0 && {
        discipline: discipline?.map(item => item.value),
      }),
    },
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    setCurrentPage(1)
  }, [brandName, discipline])

  const { data: listDisciplinesData } = useQuery(
    gql(LIST_DISCIPLINES),
    LIST_DISCIPLINES_QUERY_OPTIONS,
  )
  const disciplineOptions = listDisciplinesData?.listDisciplines
    ? formatListArray(listDisciplinesData.listDisciplines)
    : []
  const itemsPerPage = 10

  const allSaddleItems = data?.listSaddlesForAdmin?.items

  const paginatedSaddles = React.useMemo(() => {
    const items = Array.isArray(allSaddleItems) ? allSaddleItems : []
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return items.slice(startIndex, endIndex)
  }, [allSaddleItems, currentPage])

  const totalPages = Math.ceil(
    (Array.isArray(allSaddleItems) ? allSaddleItems.length : 0) / itemsPerPage,
  )

  const loadNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1)
    }
  }

  const loadPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1)
    }
  }

  const handleSort = key => {
    setSortConfig(prevConfig => {
      const direction =
        prevConfig?.key === key && prevConfig?.direction === 'ascending'
          ? 'descending'
          : 'ascending'
      return { key, direction }
    })
  }

  const handleEdit = (saddle, isViewMode = false) => {
    const encodedId = encodeURIComponent(saddle?.sk)
    navigate(`/saddles/manage/${encodedId}`, {
      state: {
        brandName: saddle?.brandName,
        isViewMode,
      },
    })
  }

  const handleDelete = async saddle => {
    setCurrentlyDeleting(saddle.sk)
    try {
      await deleteSaddle({
        variables: {
          input: {
            pk: saddle?.pk,
            sk: saddle?.sk,
          },
        },
        refetchQueries: ['listSaddlesForAdmin'],
      })
      Toast.fire({ icon: 'success', title: 'Saddle Deleted Successfully!' })
    } catch (error) {
      Toast.fire({ icon: 'error', title: 'Error deleting saddle!' })
      console.error('Error deleting saddle:', error)
    } finally {
      setCurrentlyDeleting(null)
    }
  }

  return (
    <MainLayout>
      <div className="w-full flex items-center flex-wrap justify-between mb-5 sm:mb-0 h-auto">
        <h1 className="admin-dashboard">Saddle Admin</h1>
        {!isSimpleAdmin && (
          <Button
            onClick={() => navigate('/saddles/manage')}
            className="create-saddle-btn cursor-pointer"
          >
            Create Saddle
          </Button>
        )}
      </div>

      {listSaddlesError && (
        <p
          className="text-red-600 mb-4 text-sm"
          role="alert"
        >
          Could not load saddles: {listSaddlesError.message}
        </p>
      )}

      <div className="flex flex-wrap justify-end gap-2 sm:gap-4 md:gap-6 xl:gap-8 py-4">
        <div className="w-full sm:w-1/3 md:w-1/4 xl:w-[200px]">
          <FormInput
            name="discipline"
            type="select2"
            label="Discipline"
            isMulti
            height={52}
            isClearable
            placeholder="All disciplines"
            options={disciplineOptions}
            value={discipline}
            onChange={options => setDiscipline(options || [])}
          />
        </div>

        <div className="w-full sm:w-1/3 md:w-1/4 xl:w-[200px]">
          <FormInput
            name="brandName"
            type="select2"
            label="Brand Name"
            height={52}
            placeholder="All brands"
            options={brandOptions}
            selectedValues={
              brandOptions && brandOptions.length > 0 && brandName
                ? brandOptions.find(option => option.value === brandName) || null
                : null
            }
            isClearable
            onChange={option => setBrandName(option?.value || '')}
          />
        </div>
      </div>

      {/* Saddles Table */}
      <SaddlesTable
        loading={loading}
        saddles={paginatedSaddles}
        sortConfig={sortConfig}
        handleSort={handleSort}
        canEditQuantity={!isSimpleAdmin}
        onEdit={(saddle, isViewMode) => handleEdit(saddle, isViewMode)}
        deleteSaddleLoading={deleteSaddleLoading}
        currentlyDeleting={currentlyDeleting}
        onDelete={handleDelete}
      />

      {/* Pagination */}
      <Pagination
        loadNextPage={loadNextPage}
        loadPreviousPage={loadPreviousPage}
        canLoadNext={currentPage < totalPages}
        canLoadPrevious={currentPage > 1}
      />
    </MainLayout>
  )
}

export default SaddleAdmin
